import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import DSBox from "views/system-design/DSBox";
import DSTypography from "views/system-design/DSTypography";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSButton from "views/system-design/DSButton";
import DSChip from "views/system-design/DSChip";
import DSTextField from "views/system-design/DSTextField";
import DSAlert from "views/system-design/DSAlert";
import { DSBaseTable, DSTableCell } from "views/system-design/Table";
import { Column } from "views/system-design/Table/DSBaseTable/components/DSBaseTable";
import useClocksInfiniteQuery from "../../Device/hooks/useClocksInfiniteQuery";
import {
  ContentProfileClock,
  ContentProfileDeviceStatus,
} from "../../../api/admin";
import { ClocksListResponse } from "../../../Types";
import { extractApiErrorMessage, onlineCheck } from "../../../utils/util";
import {
  CONTENT_PROFILES_QUERY_KEY,
  useAddClocksToContentProfile,
  useContentProfileClocks,
  useRemoveClocksFromContentProfile,
} from "../hooks/useContentProfiles";

interface DevicesTabProps {
  profileId: string;
  // An order profile's devices are the order's clocks, set by the API's
  // order trigger — no picker and no remove.
  orderName: string | null;
  // A ready profile's devices can't change.
  locked: boolean;
}

const STATUS_COLOR: Record<
  ContentProfileDeviceStatus,
  "default" | "warning" | "success"
> = {
  pending: "default",
  delivered: "warning",
  synced: "success",
};

const STATUS_LABEL: Record<ContentProfileDeviceStatus, string> = {
  pending: "Pending",
  delivered: "Delivered",
  synced: "Synced",
};

const pickerColumns: Column<ClocksListResponse>[] = [
  { field: "id", label: "Device ID", size: 50, sortable: false },
  { field: "idf", label: "IDF", size: 50, sortable: false },
];

const assignedColumns: Column<ContentProfileClock>[] = [
  { field: "idf", label: "IDF", size: 30, sortable: false },
  { field: "id", label: "Clock ID", size: 30, sortable: false },
  { field: "heartbeat", label: "Online", size: 20, sortable: false },
  { field: "status", label: "Sync status", size: 20, sortable: false },
];

/**
 * Add picker reuses the same mechanism ImportDevicesFavoritesDialog.tsx
 * already uses — useClocksInfiniteQuery + DSBaseTable — instead of a
 * hand-rolled search box. Clicking a row adds it immediately (addClocks is
 * idempotent server-side, so re-clicking an already-assigned device is a
 * harmless no-op).
 *
 * The assigned-devices table shows every currently assigned device
 * immediately (no search needed first — the query runs with an empty
 * search by default), with two distinct status signals per the plan: the
 * device's own online/heartbeat state (onlineCheck(), the same helper the
 * main Devices list's "online" dot already uses) and this profile's
 * separately-derived sync status (pending/delivered/synced).
 */
const DevicesTab = ({ profileId, orderName, locked }: DevicesTabProps) => {
  const [pickerQuery, setPickerQuery] = useState("");
  const {
    data: pickerResults,
    isLoading: isPickerLoading,
    fetchNextPage,
    hasNextPage,
  } = useClocksInfiniteQuery({ query: pickerQuery, registrationStatus: "ALL" });

  const [addError, setAddError] = useState<string | null>(null);
  const { mutate: addClocks, isPending: isAdding } =
    useAddClocksToContentProfile();

  const handleAdd = (clock: ClocksListResponse) => {
    setAddError(null);
    addClocks(
      { id: profileId, clockIds: [clock.id] },
      {
        onError: (e) => setAddError(extractApiErrorMessage(e, "Failed to add")),
      },
    );
  };

  const queryClient = useQueryClient();
  const { data, isLoading, isFetching } = useContentProfileClocks(profileId);
  const { mutate: removeClocks } = useRemoveClocksFromContentProfile();
  const [removingId, setRemovingId] = useState<string | null>(null);
  const clocks = data?.clocks ?? [];

  const handleRemove = (clockId: string) => {
    setRemovingId(clockId);
    removeClocks(
      { id: profileId, clockIds: [clockId] },
      { onSettled: () => setRemovingId(null) },
    );
  };

  const assignedRowBuilder = (clock: ContentProfileClock) => {
    const online = onlineCheck(
      clock.heartbeat ? new Date(clock.heartbeat) : undefined,
    );
    return (
      <>
        <DSTableCell key="idf">{clock.idf ?? "—"}</DSTableCell>
        <DSTableCell key="id">
          <DSTypography variant="caption">{clock.id}</DSTypography>
        </DSTableCell>
        <DSTableCell key="heartbeat">
          <DSChip
            size="small"
            label={online ? "Online" : "Offline"}
            color={online ? "success" : "default"}
          />
        </DSTableCell>
        <DSTableCell key="status">
          <DSChip
            size="small"
            label={STATUS_LABEL[clock.status]}
            color={STATUS_COLOR[clock.status]}
          />
        </DSTableCell>
      </>
    );
  };

  return (
    <DSBox>
      {orderName ? (
        <DSAlert severity="info" sx={{ mb: 3 }}>
          This profile targets order {orderName}. Its devices are the order's
          clocks, added automatically once the warehouse attaches them.
        </DSAlert>
      ) : locked ? null : (
        <DSBox sx={{ mb: 4 }}>
          <DSTypography variant="subtitle2" sx={{ mb: 1 }}>
            Add a device — click a row to add it
          </DSTypography>
          <DSTextField
            size="small"
            fullWidth
            placeholder="Search by IDF or clock ID"
            value={pickerQuery}
            onChange={(e) => setPickerQuery(e.target.value)}
            sx={{ mb: 1 }}
          />
          {addError && (
            <DSTypography
              variant="caption"
              color="error"
              sx={{ display: "block", mb: 1 }}
            >
              {addError}
            </DSTypography>
          )}
          {isPickerLoading && (!pickerResults || pickerResults.length === 0) ? (
            <DSBox
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                py: 6,
              }}
            >
              <DSCircularProgress />
              <DSTypography color="text.secondary">
                Loading devices…
              </DSTypography>
            </DSBox>
          ) : (
            <DSBox sx={{ maxHeight: 280, overflow: "auto" }}>
              <DSBaseTable
                data={pickerResults ?? []}
                columns={pickerColumns}
                onPressRow={isAdding ? undefined : handleAdd}
                fetchMore={hasNextPage ? fetchNextPage : undefined}
                defaultRowsPerPage={10}
              />
            </DSBox>
          )}
        </DSBox>
      )}

      <DSBox
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 1,
        }}
      >
        <DSTypography variant="subtitle2">
          Assigned devices {clocks.length > 0 ? `(${clocks.length})` : ""}
        </DSTypography>
        {isFetching && <DSCircularProgress size={16} />}
        <DSButton
          variant="outlined"
          size="small"
          disabled={isFetching}
          onClick={() =>
            queryClient.invalidateQueries({
              queryKey: [...CONTENT_PROFILES_QUERY_KEY, profileId, "clocks"],
            })
          }
        >
          Refresh
        </DSButton>
      </DSBox>
      {isLoading ? (
        <DSBox sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <DSCircularProgress />
        </DSBox>
      ) : clocks.length === 0 ? (
        <DSTypography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          {orderName
            ? "The order has no clocks yet."
            : "No devices assigned yet — add some above."}
        </DSTypography>
      ) : (
        <DSBaseTable
          data={clocks}
          columns={assignedColumns}
          rowBuilder={assignedRowBuilder}
          onPressDelete={
            orderName || locked ? undefined : (clock) => handleRemove(clock.id)
          }
          deletingRowId={removingId}
          getRowId={(clock) => clock.id}
          defaultRowsPerPage={20}
        />
      )}
    </DSBox>
  );
};

export default DevicesTab;
